import { useState } from "react";
import { Link } from "react-router-dom";
import { ImageOff, Plus } from "lucide-react";
import { formatZAR, useShop } from "./ShopContext";

export const PriceBlock = ({ p, size = "md" }) => {
  const big = size === "lg";
  if (p.your_price != null) {
    const save = Math.round((1 - p.your_price / p.price) * 1000) / 10;
    return (
      <div className="flex flex-col items-start gap-0.5" data-testid="shop-price-block">
        <span className="text-xs text-slate-400 line-through">{formatZAR(p.price)}</span>
        <span className={`${big ? "text-3xl" : "text-lg"} font-extrabold leading-tight text-emerald-700`} data-testid="shop-your-price">{formatZAR(p.your_price)}</span>
        <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white">Your price −{save}%</span>
      </div>
    );
  }
  return <span className={`${big ? "text-3xl" : "text-lg"} font-extrabold text-slate-900`} data-testid="shop-price">{formatZAR(p.price)}</span>;
};

export const StockPill = ({ p }) => {
  const tone = p.available > 10 ? "bg-emerald-50 text-emerald-700" : p.available > 0 ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700";
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{p.available > 0 ? `${p.available} in store` : "Out of stock"}</span>;
};

export const ProductImage = ({ src, alt, className = "" }) => {
  const [broken, setBroken] = useState(false);
  return src && !broken ? (
    <img src={src} alt={alt} loading="lazy" className={`h-full w-full object-contain ${className}`} onError={() => setBroken(true)} />
  ) : (
    <div className="flex h-full w-full items-center justify-center text-slate-300"><ImageOff className="h-1/3 max-h-10 w-1/3 max-w-10" /></div>
  );
};

export const ProductCard = ({ p }) => {
  const { addToCart } = useShop();
  return (
    <div className="group flex flex-col rounded-xl border border-slate-200 bg-white p-3 transition-shadow duration-200 hover:shadow-lg" data-testid={`shop-product-card-${p.plu_code}`}>
      <Link to={`/shop/p/${p.id}`} className="block">
        <div className="mb-3 aspect-square overflow-hidden rounded-lg bg-slate-50 p-2">
          <ProductImage src={p.image_url} alt={p.name} className="transition-transform duration-300 group-hover:scale-105" />
        </div>
        {p.department && <p className="text-[11px] font-semibold uppercase tracking-wider text-[#D96B27]">{p.department}</p>}
        <h3 className="line-clamp-2 min-h-[2.5rem] text-sm font-semibold text-slate-800">{p.name}</h3>
      </Link>
      <div className="mt-2 flex-1"><PriceBlock p={p} /></div>
      <div className="mt-2 flex items-center justify-between">
        <StockPill p={p} />
        <button
          type="button"
          disabled={p.available <= 0}
          onClick={() => addToCart(p)}
          className="inline-flex items-center gap-1 rounded-full bg-[#D96B27] px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-[#C2591B] disabled:bg-slate-300"
          data-testid={`shop-add-to-cart-${p.plu_code}`}
        >
          <Plus className="h-3.5 w-3.5" /> Add
        </button>
      </div>
    </div>
  );
};
